// exportFormattedReferrals
//
// Builds the "formatted referral" spreadsheet layout (Quick Refer export
// shape) from cached or live VitalEngine referral rows. See
// REFERRAL-FIELD-NAMING.md for sending_* / creating_* / receiving_*.

import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { buildFilenameStem, triggerDownload } from './exportData';
import { unwrapDateLike, extractBirthYear } from './dateUtils';
import {
  exportDicomCount,
  exportDocumentCount,
  exportTags,
} from './referralAttachments';
import { referralTypeLabel } from './referralTypeLabel';

/** Column order matches the VitalEngine formatted referral export. */
export const FORMATTED_REFERRAL_HEADERS = [
  'Referral Date',
  'Referring Facility',
  'Referring Specialty',
  'Referring Provider',
  'Creating Facility',
  'Creating Specialty',
  'Creating Provider/User',
  'Patient First Name',
  'Patient Last Name',
  'Patient DOB',
  'Receiving Facility',
  'Receiving Specialty',
  'Receiving Provider/User',
  'Referral Reason',
  'Referral Type',
  'Referral Number',
  'DICOM Count',
  'Document Count',
  'Tags',
];

const DEIDENTIFIED_DOB_HEADER = 'Patient Birth Year';

const REFERRAL_DATA_TYPES = new Set(['referrals', 'vitalrounds']);

export function isFormattedReferralExportType(dataType) {
  return REFERRAL_DATA_TYPES.has(dataType);
}

/** ISO string, SF date object, or timestamp -> 'M/D/YYYY' for export cells. */
export function formatExportDate(value) {
  const raw = unwrapDateLike(value);
  if (raw == null) return '';

  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return '';
    const isoDate = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoDate) {
      return `${parseInt(isoDate[2], 10)}/${parseInt(isoDate[3], 10)}/${isoDate[1]}`;
    }
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  }

  const d = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

function field(record, key) {
  const inner = record?.data || record?.Data || {};
  const v = inner[key];
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

/**
 * Prefer submission_date when present (VE export column), else created_date,
 * else the cache row timestamp.
 */
function referralDate(record) {
  const inner = record?.data || record?.Data || {};
  const raw =
    inner.submission_date ||
    inner.created_date ||
    record?.timestamp;
  return formatExportDate(raw);
}

/**
 * Map one cache/API record to a flat row keyed by FORMATTED_REFERRAL_HEADERS.
 * Referring_* columns use canonical sending_* (patient source).
 */
export function mapRecordToFormattedReferralRow(record, options = {}) {
  const includePhi = options.includePhi === true;
  const inner = record?.data || record?.Data || {};
  const dobRaw = inner.patient_dob ?? inner.patient_date;
  const dobValue = includePhi
    ? formatExportDate(dobRaw)
    : extractBirthYear(dobRaw);

  return {
    'Referral Date': referralDate(record),
    'Referring Facility': field(record, 'sending_facility'),
    'Referring Specialty': field(record, 'sending_speciality'),
    'Referring Provider': field(record, 'sending_user_name'),
    'Creating Facility': field(record, 'creating_facility'),
    'Creating Specialty': field(record, 'creating_speciality'),
    'Creating Provider/User': field(record, 'creating_user_name'),
    'Patient First Name': field(record, 'patient_first_name'),
    'Patient Last Name': field(record, 'patient_last_name'),
    'Patient DOB': dobValue,
    'Receiving Facility': field(record, 'receiving_facility'),
    'Receiving Specialty': field(record, 'receiving_speciality'),
    'Receiving Provider/User': field(record, 'receiving_user_name'),
    'Referral Reason': field(record, 'referral_for'),
    'Referral Type': referralTypeLabel(record),
    'Referral Number': field(record, 'referral_number'),
    'DICOM Count': exportDicomCount(record),
    'Document Count': exportDocumentCount(record),
    'Tags': exportTags(record),
  };
}

function headersForExport(includePhi) {
  if (includePhi) return FORMATTED_REFERRAL_HEADERS;
  return FORMATTED_REFERRAL_HEADERS.map(h =>
    h === 'Patient DOB' ? DEIDENTIFIED_DOB_HEADER : h,
  );
}

function rowForExport(row, includePhi) {
  if (includePhi) return row;
  const out = { ...row };
  if (Object.prototype.hasOwnProperty.call(out, 'Patient DOB')) {
    out[DEIDENTIFIED_DOB_HEADER] = out['Patient DOB'];
    delete out['Patient DOB'];
  }
  return out;
}

export function mapRecordsToFormattedReferralRows(records, options = {}) {
  return (records || []).map(r => mapRecordToFormattedReferralRow(r, options));
}

function filenameStem(dataType, startDate, endDate, includePhi) {
  const phiTag = includePhi ? 'formatted_phi' : 'formatted';
  return buildFilenameStem(dataType, startDate, endDate, phiTag);
}

export function exportFormattedReferralsToCsv(records, options = {}) {
  const includePhi = options.includePhi === true;
  const headers = headersForExport(includePhi);
  const rows = mapRecordsToFormattedReferralRows(records, options).map(r => rowForExport(r, includePhi));
  const objects = rows.map(r => {
    const o = {};
    headers.forEach(h => { o[h] = r[h] ?? ''; });
    return o;
  });
  const csv = Papa.unparse({ fields: headers, data: objects });
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, `${filenameStem(options.dataType, options.startDate, options.endDate, options.includePhi)}.csv`);
}

export function exportFormattedReferralsToXlsx(records, options = {}) {
  const includePhi = options.includePhi === true;
  const headers = headersForExport(includePhi);
  const rows = mapRecordsToFormattedReferralRows(records, options).map(r => rowForExport(r, includePhi));
  const objects = rows.map(r => {
    const o = {};
    headers.forEach(h => { o[h] = r[h] ?? ''; });
    return o;
  });
  const sheet = XLSX.utils.json_to_sheet(objects, { header: headers });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, 'Referrals');
  const arrayBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([arrayBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, `${filenameStem(options.dataType, options.startDate, options.endDate, options.includePhi)}.xlsx`);
}
